import React from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { CORE_PILLARS, COMPANY } from "@/data/site";
import { ArrowUpRight, Sun, GraduationCap, HeartHandshake, Lightbulb } from "lucide-react";
import Seo from "@/components/Seo";

const FOCUS = [
  {
    icon: Sun,
    title: "Solar lighting for schools",
    body: "Solar street lights & classroom backup lighting for under-served government schools, enabling safer evenings and uninterrupted study sessions.",
  },
  {
    icon: HeartHandshake,
    title: "Village & community electrification",
    body: "Standalone solar lighting for community halls, public squares and rural pathways — installed and maintained by our field team.",
  },
  {
    icon: GraduationCap,
    title: "Student innovation projects",
    body: "Supply, mentoring and co-build of lithium-battery & EV projects for engineering and polytechnic students across Telangana.",
  },
  {
    icon: Lightbulb,
    title: "Skilling & workshops",
    body: "On-site workshops on lithium safety, BMS design and solar O&M for technicians, ITIs and partner NGOs.",
  },
];

export default function CSR() {
  const csr = CORE_PILLARS.find((p) => p.id === "csr");
  return (
    <div data-testid="page-csr">
      <Seo
        path="/csr"
        title="CSR Projects"
        description="Partner with VPS for CSR-led clean-energy projects — solar lighting for schools, village electrification, student innovation projects and skilling workshops across India."
      />
      <PageHeader
        eyebrow="CSR"
        title="Clean energy, delivered to communities."
        subtitle="Our CSR programme partners with corporates, foundations and government bodies to extend lithium and solar infrastructure to schools, villages and public spaces."
        testId="csr-page-header"
      />

      <section className="py-16 md:py-24 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="industrial-card overflow-hidden">
              <img src={csr.image} alt="CSR" className="w-full h-[400px] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="eyebrow">Why partner with VPS</div>
            <h2 className="font-display text-3xl md:text-4xl uppercase mt-3 tracking-tighter leading-[1.05]">
              A turnkey CSR partner — from concept to handover.
            </h2>
            <p className="mt-5 text-zinc-300 leading-relaxed">
              Companies often have CSR budgets but no on-ground execution
              partner. VPS handles site survey, design, equipment manufacture,
              installation, beneficiary documentation and post-handover AMC —
              giving sponsors a single point of accountability and a measurable
              impact story.
            </p>
            <Link to="/contact" className="btn-primary mt-7" data-testid="csr-partner-cta">
              Partner with us <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="eyebrow">Focus areas</div>
          <h2 className="font-display text-3xl md:text-5xl uppercase mt-3 tracking-tighter">
            Where we deploy.
          </h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {FOCUS.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className="industrial-card p-7" data-testid={`csr-focus-${i}`}>
                  <Icon size={22} className="text-[#e63946]" />
                  <div className="font-display text-xl uppercase mt-4 leading-tight">
                    {f.title}
                  </div>
                  <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
                    {f.body}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-16 industrial-card p-8 md:p-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-center" data-testid="csr-contact-block">
            <div className="md:col-span-2">
              <div className="eyebrow">Start a CSR project</div>
              <h3 className="font-display text-2xl md:text-3xl uppercase mt-3 leading-tight">
                Have a CSR budget allocated for clean-energy infrastructure?
              </h3>
              <p className="text-zinc-400 text-sm mt-3 max-w-xl">
                Talk to us about your sponsor goals — we&apos;ll come back with a
                site shortlist, scope and indicative budget.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/contact" className="btn-primary justify-center" data-testid="csr-contact-cta">
                Get in touch
              </Link>
              <a href={`mailto:${COMPANY.email}`} className="btn-secondary justify-center text-center" data-testid="csr-email-cta">
                Email CSR team
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
